import { Link, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../core/useAuth';
import { locationPath } from '../core/returnTo';

export const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading, error } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();

  /*
   * Only while there is nothing to protect yet.
   *
   * `isLoading` does not only mean "the session is still unknown": the
   * OIDC library raises it for every navigator it starts, including
   * the silent renew that answers a 401 in the background. Swapping
   * the dashboard for a loading line at that moment unmounts
   * everything below it — the page and its state, and the news dialog
   * on top of it, which is how a freshly loaded build could greet the
   * reader and vanish again a moment later. A session that is already
   * usable stays on screen while it is being renewed.
   */
  if (isLoading && !isAuthenticated) return <div>{t('common.loading')}</div>;

  if (error) {
    /* never a dead end — from here the user must reach the login again */
    return (
      <div className="auth-error">
        <h2>{t('auth.errorTitle')}</h2>
        <pre>{error.message}</pre>
        <Link to="/login" state={{ returnTo: locationPath(location) }}>
          {t('auth.backToLogin')}
        </Link>
      </div>
    );
  }

  /*
      Key rule:
      Do not call side effects
      (like login redirects) during render.
    */
  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        state={{ returnTo: locationPath(location) }}
        replace
      />
    );
  }

  return children;
};
